import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { CardHeader } from "../../../components/cards";
import { TaskPriority, TaskStatus } from "../../../__generated__/globalTypes";
import { useTasks, useCreateTask } from "../hooks";
import { TaskItem } from "./task-item";
import { taskPriorityLabel } from "../../../helpers/enum-labels";

const PRIORITY_STYLES: Record<TaskPriority, string> = {
  [TaskPriority.Low]: "bg-gray-100 text-gray-600",
  [TaskPriority.Medium]: "bg-amber-100 text-amber-700",
  [TaskPriority.High]: "bg-red-100 text-red-700",
};

const PRIORITY_STYLES_ACTIVE: Record<TaskPriority, string> = {
  [TaskPriority.Low]: "bg-gray-600 text-white",
  [TaskPriority.Medium]: "bg-amber-600 text-white",
  [TaskPriority.High]: "bg-red-600 text-white",
};

interface IProps {
  userId: number;
}

const MAX_VISIBLE = 5;

export const TasksWidget: React.FC<IProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [quickTitle, setQuickTitle] = useState("");
  const { data, loading, refetch } = useTasks({ where: { userId } });

  const { form, submit, loading: creating } = useCreateTask({
    defaultValues: {
      status: TaskStatus.Todo,
      priority: "Medium" as any,
      description: "",
      userId,
    },
    onCompleted: () => {
      setQuickTitle("");
      form.reset({
        status: TaskStatus.Todo,
        priority: "Medium" as any,
        description: "",
        userId,
      });
      refetch();
      toast.success("Tâche ajoutée");
    },
  });

  const visibleTasks = useMemo(() => {
    const tasks = data?.tasks?.results ?? [];
    return [...tasks]
      .filter((task) => task.status !== TaskStatus.Done)
      .sort((a, b) => {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      })
      .slice(0, MAX_VISIBLE);
  }, [data]);

  const handleQuickAdd = () => {
    if (!quickTitle.trim()) return;
    form.setValue("title", quickTitle.trim());
    submit();
  };

  const priority = form.watch("priority");

  return (
    <div className="card">
      <CardHeader
        title="Mes tâches"
        button={{ title: "Voir tout", url: "/tasks" }}
      />

      <div className="flex gap-2 mb-2">
        <input
          className="input w-full rounded"
          placeholder="Ajouter une tâche rapide..."
          value={quickTitle}
          onChange={(e) => setQuickTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleQuickAdd();
          }}
          disabled={creating}
        />
      </div>

      <div className="flex gap-1.5 mb-3">
        {Object.keys(TaskPriority).map((key) => {
          const value = key as TaskPriority;
          const isActive = priority === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => form.setValue("priority", value)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                isActive ? PRIORITY_STYLES_ACTIVE[value] : PRIORITY_STYLES[value]
              }`}
            >
              {taskPriorityLabel(value)}
            </button>
          );
        })}
      </div>

      {loading ? (
        <Loading />
      ) : !visibleTasks.length ? (
        <EmptyList text="Aucune tâche en cours" />
      ) : (
        <div>
          {visibleTasks.map((task) => (
            <TaskItem key={`task-${task.id}`} task={task} onChanged={refetch} />
          ))}
        </div>
      )}
    </div>
  );
};
